<template>
  <div class="history">
    <v-base-card>
      <template v-slot:heading>
        再組版
      </template>

      <template v-slot:content>
        <template v-if="request && !request.message">
          <v-tabs v-model="tab" color="secondary" class="px-6">
            <v-tab>再組版設定</v-tab>
            <v-tab>
              配点
              <!-- 配点を変更中（未保存）の目印 -->
              <span v-if="allotChanged" class="allot-tab__changed">●</span>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <v-tab-item>
              <div class="pt-4">
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-text-field
                      v-model="request.code"
                      label="発行コード"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-text-field
                      v-model="sheetName"
                      label="テスト名"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-text-field
                      v-model="request.preset_name"
                      label="プリセット名"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-text-field
                      v-model="request.school_name"
                      label="学校"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-text-field
                      v-model="request.subject_name"
                      label="教科"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-text-field
                      v-model="request.grade_name"
                      label="教科"
                      disabled
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-select
                      v-model="answerReductionType"
                      :items="answerReductionTypeOptions"
                      item-text="text"
                      item-value="value"
                      label="解答用紙の縮小方法"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row class="mx-6">
                  <v-col cols="6">
                    <v-select
                      v-model="printType"
                      :items="printTypeOptions"
                      item-text="text"
                      item-value="value"
                      label="印刷タイプ"
                    ></v-select>
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <!-- 注文後に依頼された配点の修正。保存すると再組版に反映される -->
            <v-tab-item>
              <div class="mx-6 mt-4">
                <p class="text-caption mb-2">
                  配点は1解答あたりの点数です（小問の点数＝配点×解答数）。変更した小問は色付きで、変更前の値を表示します。
                </p>
                <v-simple-table dense class="allot-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-center">大問</th>
                        <th class="text-center">小問</th>
                        <th>配点 × 解答数</th>
                        <th>観点</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(page, pageIndex) in allotPages">
                        <tr
                          v-for="(sheetQuestion, questionIndex) in page.sheet_questions"
                          :key="sheetQuestion.id"
                          :class="{ 'allot-table__row--changed': isAllotRowChanged(sheetQuestion) }"
                        >
                          <td v-if="questionIndex === 0" :rowspan="page.sheet_questions.length" class="text-center">
                            {{ pageIndex + 1 }}
                            <div class="text-caption grey--text">{{ page.content_code }}</div>
                            <!-- 大問の問題画像。クリックで拡大表示する -->
                            <img
                              v-if="page.question_image_urls && page.question_image_urls.length"
                              :src="page.question_image_urls[0]"
                              :alt="`大問${pageIndex + 1}`"
                              class="allot-table__thumbnail"
                              @click="openContentImage(page, pageIndex)"
                            >
                          </td>
                          <td class="text-center">{{ questionNo(sheetQuestion) }}</td>
                          <td>
                            <div class="d-flex align-center">
                              <v-text-field
                                v-model="sheetQuestion.allot"
                                dense
                                outlined
                                hide-details
                                class="allot-table__input"
                                oninput="value = value.replace(/[^0-9]+/g, '');"
                              />
                              <span class="ml-2">点 × {{ sheetQuestion.question_cnt }} ＝ {{ (Number(sheetQuestion.allot) || 0) * sheetQuestion.question_cnt }}点</span>
                              <span v-if="originalAllots[sheetQuestion.id].allot !== Number(sheetQuestion.allot)" class="ml-2 text-caption grey--text">
                                （変更前 {{ originalAllots[sheetQuestion.id].allot }}点）
                              </span>
                            </div>
                          </td>
                          <td>
                            <div class="d-flex align-center">
                              <v-select
                                v-model="sheetQuestion.point_id"
                                :items="points"
                                item-text="name"
                                item-value="id"
                                dense
                                outlined
                                hide-details
                                class="allot-table__select"
                              />
                              <span v-if="originalAllots[sheetQuestion.id].point_id !== sheetQuestion.point_id" class="ml-2 text-caption grey--text">
                                （変更前 {{ pointName(originalAllots[sheetQuestion.id].point_id) }}）
                              </span>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- 観点ごとの合計と総得点 -->
                <div class="d-flex align-center flex-wrap mt-4" style="gap: 16px">
                  <span v-for="total in pointTotals" :key="total.id" class="text-body-2">{{ total.name }}：{{ total.score }}点</span>
                  <span class="text-body-2 font-weight-bold">合計：{{ totalScore }}点<template v-if="allotMaxPoint"> ／ 満点 {{ allotMaxPoint }}点</template></span>
                  <v-btn small outlined :disabled="!allotChanged" @click="resetAllots">変更を元に戻す</v-btn>
                </div>
                <v-alert v-if="allotMaxPoint && totalScore !== allotMaxPoint" type="warning" dense outlined class="mt-3">
                  合計（{{ totalScore }}点）が、テスト作成時の満点（{{ allotMaxPoint }}点）と一致していません。
                </v-alert>
              </div>
            </v-tab-item>
          </v-tabs-items>

          <v-btn class="mt-6 ml-6" small color="secondary" :dark="submitFlg" @click.stop="onSubmit" :disabled="!submitFlg">実行</v-btn>

          <!-- 大問の問題画像の拡大表示 -->
          <v-dialog v-model="contentImageDialog.show" max-width="900">
            <v-card>
              <v-card-title class="text-subtitle-1">
                大問{{ contentImageDialog.title }}
                <v-spacer />
                <v-btn icon @click="contentImageDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
              </v-card-title>
              <v-card-text>
                <img
                  v-for="url in contentImageDialog.urls"
                  :key="url"
                  :src="url"
                  :alt="`大問${contentImageDialog.title}`"
                  class="content-image-dialog__image"
                >
              </v-card-text>
            </v-card>
          </v-dialog>

          <v-confirm-dialog
            ref="allotConfirm"
            title="配点の保存"
            message="配点が変更されています。保存して再組版しますか？"
            buttonMessage="はい"
            cancelButtonMessage="いいえ"
            @confirm="saveAllotsAndSubmit"
          />
        </template>
        <template v-else-if="request">
          <p style="font-size: 14px">{{request.message}}</p>
        </template>
      </template>
    </v-base-card>
  </div>
</template>

<script>
import VBaseCard from '@/components/molecules/BaseCard'
import VConfirmDialog from '@/components/atoms/ConfirmDialog'
import { saveAs } from 'file-saver'

export default {
  components: {
    VBaseCard,
    VConfirmDialog,
  },

  data () {
    return {
      request: {},
      tab: 0,
      // 配点タブ: 編集中の大問・小問と、変更前の配点・観点（小問IDごと）
      allotPages: [],
      originalAllots: {},
      allotMaxPoint: null,
      contentImageDialog: { show: false, title: '', urls: [] },
      sheetName: '',
      submitFlg: true,
      answerReductionType: null,
      printType: 'print',
      answerReductionTypeOptions: [
        {text: '縮小なし（自動）', value: null},
        {text: '１枚で縮小をかける', value: '1'},
        {text: '両面印刷で縮小をかけない', value: '2'},
      ],
      printTypeOptions: [
        {text: 'プリント型', value: 'print'},
        {text: '冊子型', value: 'booklet'},
      ],
    }
  },

  async mounted(){
    const res = await this.$axios.get(`/api/v1/admin/requests/${this.$route.params.request_code}`)
    this.request = res.data
    this.sheetName = this.request.sheet_name
    this.answerReductionType = this.request.answer_reduction_type
    this.printType = this.request.print_type || 'print'
    if (this.request.id) await this.loadAllots()
  },

  computed: {
    points() {
      return this.$store.state.master.allPoints || []
    },
    allSheetQuestions() {
      return this.allotPages.flatMap(page => page.sheet_questions)
    },
    changedSheetQuestions() {
      return this.allSheetQuestions.filter(sheetQuestion => this.isAllotRowChanged(sheetQuestion))
    },
    allotChanged() {
      return this.changedSheetQuestions.length > 0
    },
    totalScore() {
      return this.allSheetQuestions.reduce((sum, sq) => sum + (Number(sq.allot) || 0) * sq.question_cnt, 0)
    },
    pointTotals() {
      return this.points
        .map(point => ({
          id: point.id,
          name: point.name,
          score: this.allSheetQuestions
            .filter(sq => sq.point_id === point.id)
            .reduce((sum, sq) => sum + (Number(sq.allot) || 0) * sq.question_cnt, 0),
        }))
        .filter(total => total.score > 0)
    },
  },

  methods: {
    async loadAllots() {
      const res = await this.$axios.get(`/api/v1/admin/requests/${this.request.id}/allots`)
      this.setAllots(res.data)
    },
    setAllots(data) {
      this.allotMaxPoint = data.max_point
      this.allotPages = data.sheet_content_pages
      const originals = {}
      this.allSheetQuestions.forEach(sq => { originals[sq.id] = { allot: sq.allot, point_id: sq.point_id } })
      this.originalAllots = originals
    },
    isAllotRowChanged(sheetQuestion) {
      const original = this.originalAllots[sheetQuestion.id]
      return !!original && (Number(sheetQuestion.allot) !== original.allot || sheetQuestion.point_id !== original.point_id)
    },
    resetAllots() {
      this.allSheetQuestions.forEach(sq => {
        sq.allot = this.originalAllots[sq.id].allot
        sq.point_id = this.originalAllots[sq.id].point_id
      })
    },
    questionNo(sheetQuestion) {
      return sheetQuestion.sub_number ? `${sheetQuestion.number}-${sheetQuestion.sub_number}` : String(sheetQuestion.number)
    },
    openContentImage(page, pageIndex) {
      this.contentImageDialog = { show: true, title: `${pageIndex + 1}（${page.content_code}）`, urls: page.question_image_urls }
    },
    pointName(pointId) {
      return this.points.find(point => point.id === pointId)?.name || ''
    },
    // 配点を変更したまま実行した場合は、保存してから再組版するか確認する（保存し忘れ・意図しない保存を防ぐ）
    onSubmit() {
      if (!this.allotChanged) return this.submit()
      if (this.allSheetQuestions.some(sq => !/^[1-9]\d*$/.test(String(sq.allot)))) {
        this.tab = 1
        this.$toasted.global.error({ message: '配点は1以上の整数で入力してください' })
        return
      }
      this.$refs.allotConfirm.open()
    },
    async saveAllotsAndSubmit() {
      try {
        const res = await this.$axios.patch(`/api/v1/admin/requests/${this.request.id}/update_allots`, {
          sheet_questions: this.changedSheetQuestions.map(sq => ({ id: sq.id, allot: Number(sq.allot), point_id: sq.point_id })),
        })
        this.setAllots(res.data)
        this.$toasted.global.success({ message: '配点を保存しました' })
      } catch (e) {
        const message = e.response?.data?.messages || '配点の保存に失敗しました'
        this.$toasted.global.error({ message })
        return
      }
      await this.submit()
    },
    async submit(){
      this.$app.$refs.loading.open({type: 'normal', message: '処理中です。少々お待ちください。'})
      this.submitFlg = false
      const params = new URLSearchParams({
        sheet_name: this.sheetName,
        answer_reduction_type: this.answerReductionType == null ? '' : this.answerReductionType,
        print_type: this.printType || 'print',
      })
      const res = await this.$axios.get(`/api/v1/admin/requests/${this.request.id}/recompose?${params.toString()}`, {responseType: 'blob', dataType: 'binary'})
      const blob = new Blob([res.data], { type: res.data.type })
      //レスポンスヘッダからファイル名を取得
      const contentDisposition = res.headers['content-disposition']
      const fileName = this.getFileName(contentDisposition)
      saveAs(blob, fileName)
      this.submitFlg = true
      this.$app.$refs.loading.close()
    },
    getFileName(contentDisposition){
      let fileName = contentDisposition.substring(contentDisposition.indexOf("''") + 2,
                                                    contentDisposition.length
                                                 )
      //デコードするとスペースが"+"になるのでスペースへ置換
      fileName = decodeURI(fileName).replace(/\+/g, ' ')
      return fileName
    },
  },
}
</script>

<style lang="stylus" scoped>

.select
  font-size: 11px !important;

.allot-tab__changed
  color #e53935
  margin-left 4px
  font-size 10px

.allot-table__row--changed
  background-color #fff8e1

.allot-table__input
  max-width 80px

.allot-table__select
  max-width 220px

.allot-table__thumbnail
  display block
  width 120px
  margin 4px auto
  border 1px solid #ddd
  cursor zoom-in

.content-image-dialog__image
  display block
  width 100%
  margin-bottom 8px
</style>
